import { z } from "zod";

import { TransactionParameters, Transaction } from "../types";

/**
 * Validates an email address
 * @param email The email to validate
 * @returns Object containing valid status and potential error message
 */
export const validateEmail = (
  email: string
): { valid: boolean; error?: string } => {
  if (!email) {
    return { valid: false, error: "Email address is required" };
  }

  // Simple email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { valid: false, error: "Please enter a valid email address" };
  }

  return { valid: true };
};

/**
 * Validates transaction generation parameters
 * @param params The parameters to validate
 * @returns Object containing valid status and potential error message
 */
export const validateParameters = (
  params: Partial<TransactionParameters>
): { valid: boolean; error?: string } => {
  // Validate amount range
  if (params.amountMin !== undefined && params.amountMax !== undefined) {
    if (params.amountMin >= params.amountMax) {
      return { valid: false, error: "Min amount must be less than max amount" };
    }
  }

  // Validate date range
  if (params.bookingDateStart && params.bookingDateEnd) {
    const start = new Date(params.bookingDateStart);
    const end = new Date(params.bookingDateEnd);

    if (end < start) {
      return {
        valid: false,
        error: "End payment date cannot be before start payment date",
      };
    }
  }

  // Validate count
  if (params.count !== undefined) {
    if (params.count < 1 || params.count > 100) {
      return { valid: false, error: "Please enter a number between 1 and 100" };
    }
  }

  return { valid: true };
};

// Zod schema for validating Transaction objects for API
export const TransactionSchema = z.object({
  id: z.number(),
  amount: z.number().transform((amount) => Math.round(amount * 100)), // Convert from euros to cents
  purpose: z.string(),
  senderName: z.string(),
  iban: z.string(),
  bookingDate: z.string().transform((date) => new Date(date).toISOString()), // Convert to ISO format
  valutaDate: z.string().transform((date) => new Date(date).toISOString()), // Convert to ISO format
  processed: z.literal(true).optional().default(true), // Add processed field with default true
});

// Email validation schema
export const EmailSchema = z.string().email("Invalid email format");

// Validate an array of transactions
export const validateTransactions = (
  transactions: Transaction[]
): z.infer<typeof TransactionSchema>[] => {
  const TransactionsArraySchema = z.array(TransactionSchema);
  return TransactionsArraySchema.parse(transactions);
};
